import React, { JSX } from 'react';
import styled, { css } from 'styled-components';
import { getYear } from 'date-fns';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import {
    RiArrowLeftDoubleFill,
    RiArrowLeftSLine,
    RiArrowRightDoubleFill,
    RiArrowRightSLine
} from 'react-icons/ri';
import { ko } from 'date-fns/locale';
import calendar from '@assets/icon/calendarIcon.svg';
import theme from '@styles/theme';

type StyledRangeCalendarProps = {
    title?: string;
    width?: number;
    margin?: string;
    startDate: Date | null;
    endDate: Date | null;
    onChange: (dates: [Date, Date]) => void;
    excludeDates?: Date[];
    ICON?: boolean;
};

const CustomHeader = ({
    date,
    changeYear,
    decreaseMonth,
    increaseMonth,
    customHeaderCount,
    monthDate
}: ReactDatePickerCustomHeaderProps) => {
    return (
        <Header>
            <ButtonGroup>
                <ArrowBox>
                    {/* {customHeaderCount === 0 && ( */}
                    <RiArrowLeftDoubleFill
                        fontSize={22}
                        onClick={() => changeYear(getYear(date) - 1)}
                    />
                    {/* )} */}
                </ArrowBox>
                <ArrowBox>
                    {/* {customHeaderCount === 0 && ( */}
                    <RiArrowLeftSLine fontSize={22} onClick={decreaseMonth} />
                    {/* )} */}
                </ArrowBox>
            </ButtonGroup>
            <Text>
                {monthDate.toLocaleString('ko-KR', {
                    month: 'long',
                    year: 'numeric'
                })}
            </Text>
            <ButtonGroup>
                <ArrowBox>
                    {/* {customHeaderCount === 1 && ( */}
                    <RiArrowRightSLine fontSize={22} onClick={increaseMonth} />
                    {/* )} */}
                </ArrowBox>
                <ArrowBox>
                    {/* {customHeaderCount === 1 && ( */}
                    <RiArrowRightDoubleFill
                        fontSize={22}
                        onClick={() => changeYear(getYear(date) + 1)}
                    />
                    {/* )} */}
                </ArrowBox>
            </ButtonGroup>
        </Header>
    );
};

const StyledRangeCalendar = ({
    width,
    title,
    margin,
    startDate = new Date(),
    endDate = new Date(),
    onChange = () => null,
    excludeDates = [],
    ICON
}: StyledRangeCalendarProps) => {
    return (
        <Wrapper $width={width} $margin={margin}>
            {title && <Title>{title} : </Title>}
            <DateBox $icon={ICON}>
                <DatePicker
                    selectsRange
                    dateFormat="yyyy-MM-dd"
                    locale={ko}
                    selected={startDate}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={onChange as (dates: any) => void}
                    monthsShown={2}
                    excludeDates={excludeDates}
                    renderCustomHeader={({
                        monthDate,
                        date,
                        changeYear,
                        changeMonth,
                        decreaseMonth,
                        increaseMonth,
                        decreaseYear,
                        increaseYear,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled,
                        prevYearButtonDisabled,
                        nextYearButtonDisabled
                    }: ReactDatePickerCustomHeaderProps): JSX.Element => {
                        return (
                            <CustomHeader
                                monthDate={monthDate}
                                date={date}
                                changeYear={changeYear}
                                changeMonth={changeMonth}
                                decreaseMonth={decreaseMonth}
                                increaseMonth={increaseMonth}
                                customHeaderCount={1}
                                decreaseYear={decreaseYear}
                                increaseYear={increaseYear}
                                prevMonthButtonDisabled={
                                    prevMonthButtonDisabled
                                }
                                nextMonthButtonDisabled={
                                    nextMonthButtonDisabled
                                }
                                prevYearButtonDisabled={prevYearButtonDisabled}
                                nextYearButtonDisabled={nextYearButtonDisabled}
                            />
                        );
                    }}
                />
            </DateBox>
        </Wrapper>
    );
};

export default StyledRangeCalendar;

const Wrapper = styled.div<{ $width?: number; $margin?: string }>`
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    display: flex;
    align-items: center;
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
`;
const Title = styled.div`
    font-size: 14px;
    color: ${theme.colors.grayFontColor};
    margin-right: 12px;
`;
const DateBox = styled.div<{ $icon?: boolean }>`
    width: 100%;
    min-width: 186px;
    ${({ $icon }) =>
        $icon &&
        css`
            background-image: url(${calendar});
            background-size: 12px 12px;
            background-repeat: no-repeat;
            background-position: top 50% right 0;
        `}

    .react-datepicker-wrapper,
    .react-datepicker__input-container {
        width: 100%;
        height: 100%;
    }

    .react-datepicker {
        width: 570px;
        padding: 20px;
        transform: translateX(-10%);
        border: none;
        background-color: ${theme.colors.whiteColor};
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
    }

    .react-datepicker__current-month {
        margin-bottom: 10px;
    }

    .react-datepicker__navigation--previous {
        // 이전 달 버튼
        top: 16px;
        left: 36px;
    }

    .react-datepicker__navigation--next {
        // 다음 달 버튼
        top: 16px;
        right: 36px;
    }

    .react-datepicker__month-container {
        // 하나의 달력
        width: 50%;
    }

    // 말풍선 삼각형
    .react-datepicker-popper[data-placement^='bottom']
        .react-datepicker__triangle::after,
    .react-datepicker-popper[data-placement^='bottom']
        .react-datepicker__triangle::before {
        display: none;
    }

    .react-datepicker__header {
        // 달력 Header
        border-bottom: none;
        background-color: ${theme.colors.whiteColor};
    }

    .react-datepicker__day--in-selecting-range {
        background-color: ${theme.colors.blackColor};
    }

    .react-datepicker__day--in-range {
        background-color: ${theme.colors.blackColor};
    }

    .react-datepicker__day--selected {
        // 선택한 날짜
        background-color: ${theme.colors.blackColor};
    }

    .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view--down-arrow,
    .react-datepicker__navigation-icon::before {
        border-color: ${theme.colors.grayFontColor};
    }

    input {
        width: 100%;
        cursor: pointer;
    }
`;
const Header = styled.div`
    width: 100%;
    height: 30px;
    padding: 0 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;
const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;
const Text = styled.span`
    font-size: 16px;
    font-family: PretendardSemiBold, sans-serif;
`;
const ArrowBox = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;
